import { httpApi } from '@api/http.api.ts';
import type { ADMIN_CENTER_DATA } from '@routes/sidebar/___shared___/api/constants.ts';
import type { AdminCenterClientsResponse } from '@routes/sidebar/admin-center/___shared___/api/adminCenter.api.ts';
import type {
    ProficiencyLevelResponse,
    RankResponse,
    RankSetResponse,
    RankShortCodeResponse,
    VesselDepartmentResponse,
} from '@routes/sidebar/admin-center/___shared___/api/rankReference.api.ts';
import type { VesselGroupsResponse } from '@routes/sidebar/admin-center/___shared___/api/vesselGroup.api.ts';
import type { VesselsResponse } from '@routes/sidebar/admin-center/___shared___/api/vessels.api.ts';
import type { VesselTypesResponse } from '@routes/sidebar/admin-center/___shared___/api/vesselTypes.api.ts';
import type { CrewUsersResponse } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/crew-users/api/crewUsers.api.ts';
import type { EntityLocationResponse } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/api/entity-location.api.ts';
import type {
    CountriesResponse,
    FlagsResponse,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/api/flags-countries.api.ts';
import type { FuelTypesResponse } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/api/fuel-type.api.ts';
import type { FuelTypeCategoriesResponse } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/api/fuel-type-category.api.ts';
import type { ShoreDepartmentsResponse } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/api/shore-departments.api.ts';
import type { ShoreDeptTypeResponse } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/api/shore-dept-type.api.ts';
import type { ShoreTeamsResponse } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/api/shore-team.api.ts';
import type { ShoreUserTitleResponse } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/api/shore-user-title.api.ts';
import type { TradingAreaResponse } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/api/trading-areas.api.ts';
import type {
    VesselEquipmentDetailsResponse,
    VesselEquipmentTypesResponse,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/api/vessel-equipment-details.api.ts';
import type { ShoreUsersResponse } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/shore-users/api/shoreUsers.api.ts';
import type { EmptyResultsDescription, PaginatedResponse } from '@types';

export type AdminCenterDataType = (typeof ADMIN_CENTER_DATA)[keyof typeof ADMIN_CENTER_DATA];

export interface AdminCenterDataRequest {
    dataToReturn: AdminCenterDataType[];
}

interface RankSetWithReference {
    rankSetId: number;
    rankReference: PaginatedResponse<RankResponse, EmptyResultsDescription>;
}

export interface AdminCenterDataResponse {
    vesselSubTypeResponse: VesselTypesResponse | null;
    vesselGroup: VesselGroupsResponse | null;
    vesselEquipmentType: VesselEquipmentTypesResponse | null;
    vesselDepartmentResponse: PaginatedResponse<VesselDepartmentResponse, EmptyResultsDescription> | null;
    vessel: VesselsResponse | null;
    shoreTeamResponse: ShoreTeamsResponse | null;
    shoreDepartmentTypeResponse: ShoreDeptTypeResponse | null;
    shoreDepartmentResponse: ShoreDepartmentsResponse | null;
    flagResponse: FlagsResponse | null;
    crewUserResponse: CrewUsersResponse | null;
    fuelTypeCategory: FuelTypeCategoriesResponse | null;
    fuelType: FuelTypesResponse | null;
    proficiencyLevelResponse: PaginatedResponse<ProficiencyLevelResponse, EmptyResultsDescription> | null;
    rankShortCodeResponse: RankShortCodeResponse[] | null;
    rankSetResponse: RankSetResponse[] | null;
    rankSetWithReference: RankSetWithReference[] | null;
    shoreUserResponse: ShoreUsersResponse | null;
    shoreUserTitleResponse: ShoreUserTitleResponse | null;
    tradingAreaResponse: TradingAreaResponse | null;
    clientListItemResponse: AdminCenterClientsResponse | null;
    clientTenantEntityLocationResponse: EntityLocationResponse | null;
    countryResponse: CountriesResponse | null;
    vesselEquipmentDetails: VesselEquipmentDetailsResponse | null;
}

// this endpoint returns all the data needed for admin-center by tenant via adding dataToReturn items as strings array
export const getApiAdminCenterData = (
    tenantAlias: string,
    payload: AdminCenterDataRequest,
): Promise<AdminCenterDataResponse> =>
    httpApi
        .post<AdminCenterDataResponse>(`admin-center/tenants/${tenantAlias}/data/search`, payload)
        .then(({ data }) => data);
